import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { gql, useApolloClient, useQuery } from "@apollo/client";
import { toast } from "sonner";
import { setPpDocScope, clearPpDocScope } from "../../lib/apollo/ppDocScope";
// eslint-disable-next-line import/no-unresolved
import {
  DocumentsProvider,
  DocumentListing,
  DocumentDetail,
  SubmitPafContainer,
  type MemberDocument,
  type PafSubmitConfig,
  type PafSubmitOutcome,
  type SubmitPafContainerMember,
} from "@astrana/ui/documents";
import { buildDocumentsHostConfig } from "./documentsHostConfig";
import { resolveDocumentsIdentity } from "./resolveDocumentsIdentity";
import { useAuthContext } from "../../auth/AuthContext";
import { getPpToken, ensurePpAccessToken, resolveIdentityEmail } from "../../auth/ppToken";
import { decodeJwtClaims, ppUserIdFromClaims } from "../../auth/jwt";
import { useActionAllowed } from "../../access/useFeatureAccess";
import { isEmbeddedInExtension } from "../../bridge/types";
import {
  MEMBER_BY_ID,
  type MemberByIdResult,
  type MemberByIdVars,
} from "../graphql/memberById";
import {
  SEARCH_PROVIDER_PORTAL_MEMBERS,
  type SearchProviderPortalMembersResponse,
} from "../graphql/providerPortalSearch";

/**
 * Split a Pathways member display name into first/last for the PP crosswalk
 * search. Handles both "Last, First" and "First [Middle] Last".
 */
function parsePathwaysName(
  name: string | undefined | null,
): { firstName: string; lastName: string } | null {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return null;
  if (trimmed.includes(",")) {
    const [last, first] = trimmed.split(",", 2).map((s) => s.trim());
    if (!last || !first) return null;
    return { firstName: first.split(/\s+/)[0]!, lastName: last };
  }
  const tokens = trimmed.split(/\s+/);
  if (tokens.length < 2) return null;
  return { firstName: tokens[0]!, lastName: tokens[tokens.length - 1]! };
}

/** Normalize a date string (MM/DD/YYYY or ISO-ish) to YYYY-MM-DD. */
function toIsoDate(value: string | undefined | null): string | undefined {
  if (!value) return undefined;
  const us = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(value);
  if (us) return `${us[3]}-${us[1]}-${us[2]}`;
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return iso ? iso[1] : undefined;
}

/** Compare two date strings by calendar day, tolerant of serialization. */
function sameDay(a: string, b: string): boolean {
  const da = toIsoDate(a) ?? new Date(a).toISOString().slice(0, 10);
  const db = toIsoDate(b) ?? new Date(b).toISOString().slice(0, 10);
  return da === db;
}

// Classification options for the Review-tab dropdown. The @astrana/ui docs lib
// takes these as a `documentClassifications` prop; care-management-gql proxies
// PP's member-document-classifications endpoint.
const GET_DOCUMENT_CLASSIFICATIONS = gql`
  query GetMemberDocumentClassifications($activeOnly: Boolean) {
    getMemberDocumentClassifications(activeOnly: $activeOnly) {
      id
      displayName
    }
  }
`;

// Same query the @astrana/ui HCC module fires (HCC_RISK_QUERY) — open HCC
// gaps feed DocumentDetail's "HCCs Needing Recapture" Coding-tab section, and
// the full open+closed payload is serialized into the OCR kickoff context
// (PP sends {"data":{"hccRisk":{openGaps,closedGaps}}} verbatim).
const HCC_OPEN_GAPS = gql`
  query DocumentsHccOpenGaps($mkey: String!, $companyId: String) {
    hccRisk: mongoRiskAdjustmentGaps(mkey: $mkey, companyId: $companyId) {
      openGaps {
        id: _id
        ccCode
        ccDescription
        ccDescriptionShort
        gapStatus
      }
      closedGaps {
        id: _id
        ccCode
        ccDescription
        ccDescriptionShort
        gapStatus
      }
    }
  }
`;

// Same query the @astrana/ui Quality Gaps module fires (QUALITY_GAPS_QUERY) —
// serialized into the OCR kickoff context so PP's AI can match findings
// against the member's open measures.
const QUALITY_GAPS = gql`
  query DocumentsQualityGaps($mkey: String!, $companyId: String) {
    mongoQualityGaps(mkey: $mkey, companyId: $companyId) {
      openGaps {
        id: _id
        submeasureCode
        gapStatus
        measureYear
        dataSource
        resultLastModifiedDt
        lastModifiedDt
        submeasureName
        submeasureDescription
        actionInfo
        measureType
        certified
      }
      closedGaps {
        id: _id
        submeasureCode
        gapStatus
        measureYear
        dataSource
        resultLastModifiedDt
        lastModifiedDt
        submeasureName
        submeasureDescription
        actionInfo
        measureType
        certified
      }
    }
  }
`;

// Category options for the upload modal's Document Category select; PP
// proxies MemberSearch/GetDocumentCategory through care-management-gql.
const GET_DOCUMENT_CATEGORIES = gql`
  query GetMemberDocumentCategories($companyId: String!, $membMpiNo: String!) {
    getMemberDocumentCategories(companyId: $companyId, membMpiNo: $membMpiNo) {
      rmiUid
      rmiCategoryCode
      rmiCategoryDesc
    }
  }
`;

/**
 * Pathways members carry source-system-prefixed MPIs ("EZCAP_EMPI|<guid>");
 * PP's REST endpoints want the bare GUID. The backend normalizes too — this
 * keeps client-side filters/headers consistent.
 */
function normalizeMpi(mpi: string): string {
  const idx = mpi.lastIndexOf("|");
  return idx >= 0 ? mpi.slice(idx + 1) : mpi;
}

/** Recursively strip Apollo's __typename before serializing for PP. */
function stripTypename<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(stripTypename) as unknown as T;
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (k !== "__typename") out[k] = stripTypename(v);
    }
    return out as unknown as T;
  }
  return value;
}

interface DocumentClassification {
  id: number;
  displayName: string;
}

interface GetClassificationsResult {
  getMemberDocumentClassifications: DocumentClassification[];
}

interface HccOpenGap {
  id: string;
  ccCode: string;
  ccDescription: string;
  ccDescriptionShort: string;
  gapStatus: string | null;
}

interface HccOpenGapsResult {
  hccRisk: {
    openGaps: HccOpenGap[] | null;
    closedGaps: HccOpenGap[] | null;
  } | null;
}

/**
 * How the host tells this page WHICH member to render. A union, so the compiler
 * rejects a call site that supplies no identity at all.
 *
 * That enforcement is the point: this page used to accept zero identity props
 * and read the route itself, and when that silently yielded nothing it rendered
 * a blank pane with no error and no network request. A dropped prop must be a
 * build failure, not an invisible runtime one.
 *
 * - `navId` — the route member id (`/members/:id`), REQUIRED as a written prop
 *   whenever the host isn't supplying a complete PP identity. It exists because
 *   `App.tsx` mounts the whole keep-alive shell under a SINGLE catch-all
 *   `<Route path="/*">` (so the shell survives /members ↔ /members/:id ↔
 *   /worklist without remounting), and that pattern declares no `:id` segment —
 *   so `useParams().id` is `undefined` for EVERY component inside the shell,
 *   not just hidden tabs. MemberProfilePage is immune because TabbedAppShell
 *   hands it `navIdOverride`; this page had no equivalent.
 *   Typed `string | undefined` rather than optional on purpose: the key must be
 *   written at the call site, while an unresolved id is still allowed through to
 *   the reported `no-member` state.
 * - `companyId` + `membMpiNo` — a complete PP identity, which skips the
 *   MEMBER_BY_ID self-fetch entirely (ProviderPortalDocumentsPage, and
 *   MemberProfilePage's `pp:` branch). `navId` is then optional but still
 *   useful, since it keys the member-switch guard.
 */
type MemberDocumentsIdentityProps =
  | { navId: string | undefined; companyId?: undefined; membMpiNo?: undefined }
  | { navId?: string; companyId: string; membMpiNo: string };

export type MemberDocumentsPageProps = MemberDocumentsIdentityProps & {
  userId?: string;
  memberId?: string;
  /** Member key for the HCC open-gaps query (falls back to the MPI). */
  mkey?: string;
  /**
   * Controlled document selection. When `onSelectedDocumentIdChange` is
   * provided the parent owns which document is open (e.g. MemberProfilePage
   * promotes the detail view to a full page outside the profile tabs);
   * otherwise selection is internal state.
   */
  selectedDocumentId?: number | null;
  onSelectedDocumentIdChange?: (documentId: number | null) => void;
  /**
   * Full-page rendering: columned listing table / side-by-side detail.
   * Defaults to true on the web app, false in the narrow extension panel.
   */
  isFullPage?: boolean;
};

/**
 * Visible "could not resolve a member for this view" state, with the reason.
 *
 * Exists because the `return null` it replaces made three very different
 * situations indistinguishable in the UI — still loading, resolved to no
 * member, and a member id that never arrived — all presenting as an inert
 * blank tab. Any future identity dead-end should render through here rather
 * than returning nothing.
 */
function DocumentsUnavailable({ detail }: { detail: string }) {
  return (
    <div className="p-6 text-sm text-muted-foreground">
      <p className="font-medium text-foreground">Documents are unavailable</p>
      <p className="mt-1">This member could not be resolved — {detail}.</p>
    </div>
  );
}

/**
 * Self-contained Documents tab. Rendered as a module inside @astrana/ui's
 * MemberProfile (passed as a React node in the tab's `modules` array), so it
 * lives in the profile's content area exactly like the other tabs — no
 * overlay. Reads the member from `props.navId` (NOT the router — see that
 * prop's doc) + Apollo cache (the parent MemberProfilePage already ran
 * MEMBER_BY_ID for the same id, so this is normally a cache hit).
 *
 * When explicit props (companyId + membMpiNo) are provided, the MEMBER_BY_ID
 * self-fetch is skipped — used by ProviderPortalDocumentsPage and the PP
 * branch of MemberProfilePage.
 *
 * Every identity outcome renders something visible. This page previously had a
 * bare `return null` for "couldn't resolve a member", which is why a broken
 * member id presented as an inert, completely blank tab with no console error
 * and no network request — undiagnosable from the UI.
 */
// No default `= {}`: an identity is mandatory (see MemberDocumentsIdentityProps),
// and `{}` satisfies neither shape.
export function MemberDocumentsPage(props: MemberDocumentsPageProps) {
  // `props.navId` first — under the tab shell's catch-all route `params.id` is
  // always undefined (see MemberDocumentsPageProps.navId). Reading the router
  // alone is what made this tab render nothing at all.
  const params = useParams<{ id: string }>();
  const id = props.navId ?? params.id;
  const { accountLabel, selectedProvider, accessToken: primarySessionToken } = useAuthContext();
  const apolloClient = useApolloClient();

  // Identity resolution is a single explicit decision with no fall-through —
  // see resolveDocumentsIdentity for the two traps that made it necessary and
  // for the unit tests that pin it.
  const identity = resolveDocumentsIdentity({
    navId: props.navId,
    routeId: params.id,
    companyId: props.companyId,
    membMpiNo: props.membMpiNo,
  });
  const hasExplicitProps = identity.mode === "explicit";

  // ── Submit PAF: PP bearer, mirroring MemberProfilePage's Create-Auth /
  // Create-Claim wiring (auth/ppToken.ts) — the one sanctioned source for a
  // PP bearer in this app; do not invent a second token path here. `ppToken`
  // is the already-active, identity-verified PP bearer if one exists.
  const identityEmail = resolveIdentityEmail(accountLabel);
  const ppToken = getPpToken(selectedProvider, primarySessionToken, identityEmail);

  // Live context for the stable getAccessToken callback below — same
  // ref-mirrors-latest-render pattern MemberProfilePage uses for its own
  // ppSessionCtxRef, so the closure never reads a stale identity/token.
  const ppSessionCtxRef = useRef({
    selectedProvider,
    sessionAccessToken: primarySessionToken,
    identityEmail,
  });
  ppSessionCtxRef.current = { selectedProvider, sessionAccessToken: primarySessionToken, identityEmail };

  // Reset on (re-)mount, not just set on unmount. The effect MUST be idempotent
  // across a cleanup→re-run cycle on the SAME instance, which React does in two
  // routine situations: StrictMode's development double-invoke (main.tsx wraps
  // the app in React.StrictMode) and every Fast Refresh. Refs survive both, so a
  // cleanup-only version latched this to `true` permanently and
  // stableGetPafAccessToken below then returned "" forever — the PAF requests
  // went out with NO Authorization header and portalManager 401'd, while
  // handleSubmitPaf still opened the form because it reads `ppToken` directly.
  const pafTokenDisposedRef = useRef(false);
  useEffect(() => {
    pafTokenDisposedRef.current = false;
    return () => {
      pafTokenDisposedRef.current = true;
    };
  }, []);

  // Mirrors MemberProfilePage's `stableGetClaimAccessToken`: try the existing
  // bearer first, else attempt one SILENT mint (never opens PpLoginModal —
  // interactive re-auth belongs on a user gesture, see handleSubmitPaf below).
  const stableGetPafAccessToken = useRef(async (): Promise<string> => {
    if (pafTokenDisposedRef.current) return "";
    const ctx = ppSessionCtxRef.current;
    const existing = getPpToken(ctx.selectedProvider, ctx.sessionAccessToken, ctx.identityEmail);
    if (existing) return existing;
    if (!ctx.identityEmail) return "";
    try {
      await ensurePpAccessToken(ctx.identityEmail);
    } catch {
      return "";
    }
    if (pafTokenDisposedRef.current) return "";
    const ctxAfter = ppSessionCtxRef.current;
    return (
      getPpToken(ctxAfter.selectedProvider, ctxAfter.sessionAccessToken, ctxAfter.identityEmail) ?? ""
    );
  }).current;

  // Persona write-gate. Submitting a PAF is a clinical write, so it follows the
  // same seam as every other write in this app (Auths/create, Claims/submit,
  // Outreach/create, Claims/printEob) rather than the Documents module's
  // `useCanUpdate`, which is still a TODO(role-gating) stub returning
  // `isAuthenticated`.
  //
  // NOTE this gate FAILS CLOSED: a persona payload that omits the Documents
  // module resolves to [], so the button stays hidden until an admin grants
  // Documents/submitPaf to that persona. That is deliberate for a clinical
  // write, but it does mean the action must be configured in the admin console
  // before anyone sees the button.
  const canSubmitPaf = useActionAllowed("Documents", "submitPaf");

  const [submitPafOpen, setSubmitPafOpen] = useState(false);
  const [submitPafPending, setSubmitPafPending] = useState(false);

  // Close the form when the profile switches member. This route does NOT
  // remount when only `:id` changes (same precedent noted for `openDocId` in
  // MemberProfilePage), so without this the open form silently re-targets to
  // the newly-selected member as `pafConfig`/`pafMember` recompute — i.e. a
  // half-filled assessment could be submitted against the wrong patient.
  // Adjust-state-during-render rather than an effect, so the stale form never
  // gets a frame with the new member's context.
  //
  // This guard was vacuous under the tab shell before `navId` existed (`id` was
  // always undefined, so the comparison never fired). It was not exploitable
  // there — TabbedAppShell keys each MemberProfilePage by `tab.navId`, so
  // switching member mounts a DIFFERENT instance and an open form can't
  // re-target. It is load-bearing in classic (non-shell) mode, where
  // `/members/:id` reuses one instance across ids, and it now works in both.
  const [pafMemberIdSeen, setPafMemberIdSeen] = useState(id);
  if (id !== pafMemberIdSeen) {
    setPafMemberIdSeen(id);
    if (submitPafOpen) setSubmitPafOpen(false);
  }

  const handleSubmitPaf = async () => {
    // Gate the ACTION, not just the affordance — hiding the button is a UI
    // convenience, not enforcement.
    if (!canSubmitPaf || submitPafPending) return;

    // AWAIT the silent mint before mounting the form. Fire-and-forget is not
    // good enough: `pafUserObjectId` derives from `ppToken`, which is read at
    // RENDER time, and nothing re-renders this component when a background mint
    // later resolves. An identity without an already-live bearer would get
    // userObjectId "" baked into pafConfig for the whole life of the mounted
    // container, and portalManager rejects every submission whose UserObjectId
    // doesn't match the token's `sub`. Mirrors MemberProfilePage awaiting
    // ensurePpSession() before it opens Create Claim.
    let token = ppToken;
    if (!token && identityEmail) {
      setSubmitPafPending(true);
      try {
        await ensurePpAccessToken(identityEmail);
        token = getPpToken(selectedProvider, primarySessionToken, identityEmail);
      } catch {
        token = undefined;
      } finally {
        setSubmitPafPending(false);
      }
    }

    if (!token) {
      // Fail loudly instead of opening into a guaranteed-failure state. Unlike
      // MemberProfilePage there is no interactive PpLoginModal to fall back to
      // here, so the honest move is to not open the form at all.
      toast.error(
        "Couldn't verify your Provider Portal session. Sign in to Provider Portal and try again.",
      );
      return;
    }

    setSubmitPafOpen(true);
  };

  // Only the self-fetch mode queries: `explicit` already has the identity, and
  // `partial`/`no-member` have nothing that could match (self-fetching a `pp:`
  // id would fire a query that can never hit a Pathways record). Both are
  // reported to the user below instead.
  const { data, loading: memberLoading } = useQuery<MemberByIdResult, MemberByIdVars>(
    MEMBER_BY_ID,
    {
      variables: { id: id ?? "" },
      skip: identity.mode !== "self-fetch",
      fetchPolicy: "cache-first",
    },
  );

  // ── Pathways → Provider Portal identity crosswalk ─────────────────────────
  //
  // Documents live in PP, keyed by PP's OWN MembMpiNo/MembId — which for
  // Pathways-sourced members does NOT match the Pathways MPI (confirmed
  // against PP dev: the Pathways "EZCAP_EMPI|<guid>" never appears in PP's
  // member table; PP has its own MPI for the same person). Querying PP with
  // the Pathways MPI silently returns nothing (doc list, categories) or 400s
  // (upload). So when the member came from Pathways search, resolve the PP
  // record by demographics (name + DOB via PP MemberSearch) and use PP's
  // identity for everything documents-related.
  const pathways = hasExplicitProps ? null : (data?.PathwaysMember ?? null);
  const nameParts = useMemo(
    () => parsePathwaysName(pathways?.name),
    [pathways?.name],
  );
  const dobIso = toIsoDate(pathways?.dob);
  const {
    data: xwalkData,
    loading: xwalkLoading,
    called: xwalkCalled,
  } = useQuery<SearchProviderPortalMembersResponse>(
    SEARCH_PROVIDER_PORTAL_MEMBERS,
    {
      variables: {
        input: {
          firstName: nameParts?.firstName ?? "",
          lastName: nameParts?.lastName ?? "",
          ...(dobIso ? { dateOfBirth: dobIso } : {}),
          // Scope the PP search to the member's own IPA. The backend searches
          // this IPA directly (X-Backend-ID) instead of fanning out over the
          // caller's PP group claim — required for SSO/service-account sessions,
          // whose token groups don't map to IPAs in prod.
          ...(pathways?.ipaCode ? { companyId: pathways.ipaCode } : {}),
        },
      },
      skip: hasExplicitProps || !pathways || !nameParts,
      fetchPolicy: "cache-first",
    },
  );
  const ppMatch = useMemo(() => {
    const rows = xwalkData?.searchProviderPortalMembers ?? [];
    if (rows.length === 0) return null;
    // Server already filtered by DOB when we had one; re-check defensively
    // (PP date serialization varies) and then disambiguate by IPA.
    const dobFiltered = dobIso
      ? rows.filter((r) => !r.dob || sameDay(r.dob, dobIso))
      : rows;
    const pool = dobFiltered.length > 0 ? dobFiltered : rows;
    const sameIpa = pool.filter((r) => r.companyId === pathways?.ipaCode);
    if (sameIpa.length === 1) return sameIpa[0];
    if (pool.length === 1) return pool[0];
    // Ambiguous — never guess which person to attach documents to.
    return null;
  }, [xwalkData, dobIso, pathways?.ipaCode]);

  const config = useMemo(
    () => buildDocumentsHostConfig(apolloClient),
    [apolloClient],
  );

  const { data: classificationsData } = useQuery<GetClassificationsResult>(
    GET_DOCUMENT_CLASSIFICATIONS,
    { variables: { activeOnly: true }, fetchPolicy: "cache-first" },
  );
  const documentClassifications =
    classificationsData?.getMemberDocumentClassifications ?? [];

  interface GetCategoriesResult {
    getMemberDocumentCategories: Array<{
      rmiUid: number;
      rmiCategoryCode: string;
      rmiCategoryDesc: string | null;
    }>;
  }

  // Controlled (parent-owned) or uncontrolled document selection.
  const [internalDocId, setInternalDocId] = useState<number | null>(null);
  const isControlled = props.onSelectedDocumentIdChange !== undefined;
  const selectedDocumentId = isControlled
    ? (props.selectedDocumentId ?? null)
    : internalDocId;
  const setSelectedDocumentId = (docId: number | null) => {
    if (isControlled) props.onSelectedDocumentIdChange?.(docId);
    else setInternalDocId(docId);
  };

  const isFullPage = props.isFullPage ?? !isEmbeddedInExtension();

  // Publish the active member's IPA + MPI so the lib's by-uid document queries
  // can be resolved server-side (see ppDocScope / client.ts). Kept as a hook
  // before the early return below so hook order stays stable.
  // For Pathways members the PP-facing scope comes from the crosswalk match —
  // the Pathways MPI/IPA are meaningless to PP. Until the crosswalk settles
  // (or when it finds nothing), leave the scope empty so PP queries skip
  // instead of firing with an identity PP doesn't recognise.
  const ppDocCompanyId = hasExplicitProps
    ? props.companyId
    : (ppMatch?.companyId ?? "");
  const ppDocMpi = normalizeMpi(
    (hasExplicitProps ? props.membMpiNo : ppMatch?.membMpiNo) ?? "",
  );
  useEffect(() => {
    setPpDocScope({
      companyId: ppDocCompanyId || undefined,
      membMpiNo: ppDocMpi || undefined,
    });
    return () => clearPpDocScope();
  }, [ppDocCompanyId, ppDocMpi]);

  // Category options for the upload modal (PP MemberSearch/GetDocumentCategory).
  const { data: categoriesData } = useQuery<GetCategoriesResult>(
    GET_DOCUMENT_CATEGORIES,
    {
      variables: { companyId: ppDocCompanyId ?? "", membMpiNo: ppDocMpi },
      skip: !ppDocCompanyId || !ppDocMpi,
      fetchPolicy: "cache-first",
    },
  );
  const documentCategories = useMemo(
    () =>
      (categoriesData?.getMemberDocumentCategories ?? []).map((c) => ({
        rmiCategoryCode: c.rmiCategoryCode,
        rmiCategoryDesc: c.rmiCategoryDesc,
      })),
    [categoriesData],
  );

  // Open HCC gaps for the detail view's Coding tab ("HCCs Needing
  // Recapture"). Same query/variables as the lib's HCC module so the section
  // matches the Home-tab HCC card. Only fetched while a document is open.
  const hccMkey =
    props.mkey ||
    data?.PathwaysMember?.memberIdOthers?.apolloMemberKey ||
    ppDocMpi ||
    props.membMpiNo ||
    "";
  const { data: hccData } = useQuery<HccOpenGapsResult>(HCC_OPEN_GAPS, {
    variables: { mkey: hccMkey, companyId: ppDocCompanyId || "" },
    skip: !hccMkey,
    fetchPolicy: "cache-first",
  });
  const patientOpenHccs = useMemo(
    () =>
      (hccData?.hccRisk?.openGaps ?? []).map((gap) => ({
        id: gap.id,
        ccCode: gap.ccCode,
        ccDescription: gap.ccDescription,
        ccDescriptionShort: gap.ccDescriptionShort,
        gapStatus: gap.gapStatus ?? undefined,
      })),
    [hccData],
  );

  // Quality gaps — only used as OCR kickoff context for the upload modal.
  const { data: qualityGapsData } = useQuery(QUALITY_GAPS, {
    variables: { mkey: hccMkey, companyId: ppDocCompanyId || "" },
    skip: !hccMkey,
    fetchPolicy: "cache-first",
  });

  // Serialize both payloads exactly the way PP's frontend does for
  // MemberDocumentOcr/kickoff: {"data":{<queryField>:{openGaps,closedGaps}}}.
  const ocrQualityGapsJson = useMemo(() => {
    const gaps = (qualityGapsData as { mongoQualityGaps?: unknown } | undefined)
      ?.mongoQualityGaps;
    return gaps
      ? JSON.stringify({ data: { mongoQualityGaps: stripTypename(gaps) } })
      : null;
  }, [qualityGapsData]);
  const ocrHccRiskJson = useMemo(
    () =>
      hccData?.hccRisk
        ? JSON.stringify({ data: { hccRisk: stripTypename(hccData.hccRisk) } })
        : null,
    [hccData],
  );

  let memberId: string;
  let membMpiNo: string;
  let companyId: string;
  let userId: string;

  if (hasExplicitProps) {
    companyId = props.companyId!;
    membMpiNo = normalizeMpi(props.membMpiNo!);
    memberId = props.memberId ?? membMpiNo;
    userId = props.userId ?? accountLabel ?? "";
  } else if (identity.mode === "partial") {
    // Half an identity from the host — never silently degrade to a self-fetch
    // that cannot succeed. See resolveDocumentsIdentity.
    return (
      <DocumentsUnavailable
        detail={
          identity.missing === "membMpiNo"
            ? "the member's Provider Portal MPI is missing"
            : "the member's Provider Portal IPA is missing"
        }
      />
    );
  } else if (identity.mode === "no-member") {
    // Previously a bare `return null`: no id reached this view at all, which is
    // exactly what the catch-all shell route produced.
    return <DocumentsUnavailable detail="no member was supplied to this view" />;
  } else {
    const member = data?.PathwaysMember;
    // Distinguish "still resolving" from "resolved to nothing" — a bare
    // `return null` used to hide BOTH, presenting as a permanently blank tab
    // with no error and no network request.
    if (memberLoading) {
      return (
        <div className="p-6 text-sm text-muted-foreground">
          Loading documents…
        </div>
      );
    }
    if (!member) {
      return (
        <DocumentsUnavailable
          detail={`no member record was found for id ${id}`}
        />
      );
    }
    // Wait for the PP crosswalk so the listing doesn't first render (empty)
    // against the unknown Pathways identity and then flip.
    if (xwalkCalled && xwalkLoading) {
      return (
        <div className="p-6 text-sm text-muted-foreground">
          Loading documents…
        </div>
      );
    }
    if (!ppMatch) {
      return (
        <div className="p-6 text-sm text-muted-foreground">
          Documents are unavailable for this member — no matching Provider
          Portal record was found
          {nameParts ? ` for ${nameParts.firstName} ${nameParts.lastName}` : ""}
          {dobIso ? ` (DOB ${dobIso})` : ""}.
        </div>
      );
    }
    // Use PP's identity end-to-end: its MPI keys the document store, its
    // MembId drives OCR kickoff + the upload-key lookup.
    memberId = ppMatch.membId ?? member.memberPolicyId ?? "";
    membMpiNo = normalizeMpi(ppMatch.membMpiNo);
    companyId = ppMatch.companyId;
    userId = accountLabel ?? "";
  }

  // The @astrana/ui DocumentDetail SKIPS its getDocumentAdditionalInfo query
  // when userId is falsy — which leaves the Summary/Quality/Coding tabs
  // spinning forever. The backend ignores this value (it derives the PP user
  // from the bearer), so any stable non-empty string is fine.
  if (!userId) userId = memberId || membMpiNo || "astraconnect";

  // ── Submit PAF: transport config + member context (Phase C wiring) ───────
  // userObjectId — portalManager's identity check requires `token.sub ==
  // UserObjectId` (see PafSubmitConfig's own doc comment); ppUserIdFromClaims
  // prefers `sub`, falling back to `oid`/`objectId` — the same claim
  // precedence MemberProfilePage uses for Create Claim's `claimUserId`.
  const pafUserObjectId = ppToken ? (ppUserIdFromClaims(decodeJwtClaims(ppToken)) ?? "") : "";
  const pafConfig: PafSubmitConfig = {
    apiBaseUrl: `${import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL}/api`,
    companyId,
    getAccessToken: stableGetPafAccessToken,
    userObjectId: pafUserObjectId,
    // frontDoorId intentionally left unset — providerPortalAPI's passthrough
    // doesn't forward X-Azure-FDID (see PafSubmitConfig.frontDoorId's own doc
    // comment); existing PP-API traffic already works without it.
  };
  // memberKeyId: the PP GUID membKeyId — PP path via `props.mkey` (plumbed by
  // MemberProfilePage from `ppMember.membKeyId`, same value the full-page
  // Documents route already passes); Pathways path via the crosswalk match's
  // own `membKeyId`. NOT SOURCED for either path: PP's `memberData.
  // IsPafRequired` and `memberType` have no member-360 equivalent today (left
  // undefined; the container degrades the same way its own "no host data"
  // fallback does — see report).
  const pafMemberKeyId = hasExplicitProps
    ? props.mkey
    : (ppMatch?.membKeyId ?? undefined);
  const pafMbi = hasExplicitProps
    ? undefined
    : (data?.PathwaysMember?.memberIdOthers?.mbi ?? undefined);
  // `mkey` is the GetMemberPafDataV2 lookup key and MUST use PP's own
  // precedence — upper-cased MemberKeyId, else MemberMbi. Verified against both
  // ends: providerPortalFrontend's PatientAssessmentFormDialog.tsx:31-33
  // (`selectedRecord.MemberKeyId.toUpperCase() : selectedRecord.MemberMbi`,
  // passed straight through as usePafForm's `mkey`), and @astrana/ui's own
  // resolveMemberId (pafFormData.ts:72-75), which already applies exactly this
  // rule to the SUBMISSION's MemberId field.
  //
  // Deliberately NOT `hccMkey`. That is the PATHWAYS `apolloMemberKey`, which
  // keys the Mongo gap queries (mongoRiskAdjustmentGaps / mongoQualityGaps) and
  // does NOT exist in PP's member table — the same Pathways-vs-PP identity split
  // the crosswalk above exists to bridge. Sending it paired with a PP `ipa` made
  // PP return an empty payload with no error. One variable was serving two
  // different key spaces; they are now separate.
  //
  // Upper-casing matters on its own: MemberProfilePage keeps a `ppMkeyUpper`
  // for the same reason — a raw-vs-upper-cased mismatch makes the lookup
  // silently never match.
  const pafMkey = pafMemberKeyId
    ? pafMemberKeyId.toUpperCase()
    : (pafMbi ?? "");
  const pafMember: SubmitPafContainerMember = {
    mkey: pafMkey,
    ipa: companyId,
    memberKeyId: pafMemberKeyId,
    mbi: pafMbi,
    lob: hasExplicitProps ? undefined : (data?.PathwaysMember?.lineOfBusiness ?? undefined),
  };
  const handlePafOutcome = (outcome: PafSubmitOutcome) => {
    // Only return to the listing on a genuine PAF-submission success — a
    // "submission-failed" outcome leaves the form open (onError already
    // toasted it) so the user doesn't lose in-progress work.
    if (outcome.status === "submission-failed") return;

    // The PAF is written over the REST passthrough, not an Apollo mutation, so
    // nothing invalidates the document-list cache on its own. Remounting the
    // listing is not sufficient on its own either — that depends on a
    // library-internal fetchPolicy this host can't see. Evict explicitly so the
    // new submission is actually visible instead of appearing only after a
    // manual reload.
    // Field name must match the library's query selection exactly
    // (`getMemberDocuments` in @astrana/ui's documents.graphql.ts) — a wrong
    // name evicts nothing and fails silently.
    apolloClient.cache.evict({ id: "ROOT_QUERY", fieldName: "getMemberDocuments" });
    apolloClient.cache.gc();

    setSubmitPafOpen(false);
  };

  return (
    <DocumentsProvider config={config}>
      {submitPafOpen ? (
        <SubmitPafContainer
          config={pafConfig}
          member={pafMember}
          onBack={() => setSubmitPafOpen(false)}
          onSaved={handlePafOutcome}
          onSubmitted={handlePafOutcome}
          onError={(message) => toast.error(message)}
        />
      ) : selectedDocumentId === null ? (
        <DocumentListing
          filter={{ companyId, membMpiNo }}
          isFullPage={isFullPage}
          documentClassifications={documentClassifications}
          documentCategories={documentCategories}
          // Submit PAF replaces the upload-a-PAF-file button — the digital
          // form's "upload a completed PAF instead" checkbox covers that case.
          // showUploadPafButton/UploadPafModal stay wired below as the
          // rollback path; only the header button is swapped here.
          showUploadPafButton={false}
          showSubmitPafButton={canSubmitPaf}
          onSubmitPaf={handleSubmitPaf}
          // Enables the upload modal's AI analysis (PP's MemberDocumentOcr
          // pipeline) with the member's gaps/risk as matching context.
          ocrParams={{
            memberId: memberId || membMpiNo,
            qualityGaps: ocrQualityGapsJson,
            hccRisk: ocrHccRiskJson,
          }}
          onDocumentClick={(doc: MemberDocument) =>
            setSelectedDocumentId(doc.rmiUid)
          }
        />
      ) : (
        <DocumentDetail
          documentId={selectedDocumentId}
          userId={userId}
          isFullPage={isFullPage}
          memberClinicalParams={{ memberId, mkey: "", mpi: membMpiNo, companyId }}
          documentClassifications={documentClassifications}
          patientOpenHccs={patientOpenHccs}
          onBack={() => setSelectedDocumentId(null)}
        />
      )}
    </DocumentsProvider>
  );
}
